import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Info } from 'lucide-react';

/**
 * FormulaReveal Component (Simplified)
 * Shows a short description tooltip on hover for any metric/chart.
 * Accepts legacy props (formula, variables, etc.) for backward compat but only renders description.
 */
export default function FormulaReveal({
  title,
  description,
  children,
  className = '',
  // Legacy props kept for backward compatibility (unused)
  type, formula, variables, dataSource, aiPrompt, methodology, references,
}) {
  const tooltipText = description || title || 'No description available';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children || (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 rounded-full hover:bg-primary/10 ${className}`}
            >
              <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
            </Button>
          )}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-sm" data-testid="chart-info-tooltip">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Pre-defined descriptions for common hotel metrics (simplified - description only)
export const HOTEL_FORMULAS = {
  OCCUPANCY_RATE: {
    title: 'Occupancy Rate',
    description: 'The percentage of available rooms that are occupied during a specific time period.',
  },
  MARKET_INSIGHT_AI: {
    title: "AI Market Analysis",
    description: "AI-generated insights analyzing your market position, pricing trends, and competitive landscape with actionable recommendations."
  },
  COMPETITIVE_ANALYSIS_AI: {
    title: "AI Competitive Analysis",
    description: "AI-powered analysis of your competitive position, pricing strategy, and market insights with personalized recommendations."
  },
  RATEPARITY_AI: {
    title: "AI Rateparity Analysis",
    description: "AI-generated analysis of rate parity patterns across OTAs, highlighting undercuts and providing recommendations to protect your direct channel revenue."
  },
  ADVANCE_ANALYTICS_AI: {
    title: "AI Market Analysis",
    description: "AI-generated recommendations for maximizing revenue based on rate parity undercuts, competitor pricing, and market demand signals."
  },
  ADR: {
    title: 'Average Daily Rate (ADR)',
    description: 'The average rental income per paid occupied room in a given time period.',
  },
  REVPAR: {
    title: 'Revenue Per Available Room (RevPAR)',
    description: 'Measures revenue generated per available room, combining occupancy and rate performance.',
  },
  MARKET_POSITION: {
    title: 'Market Position Ranking',
    description: 'Your hotel\'s price ranking among competitors, sorted from lowest to highest price.',
  },
  LOWEST_PRICE: {
    title: 'Lowest Available Rate',
    description: 'The minimum rate available across all distribution channels for a specific date.',
  },
  RATE_PARITY_UNDERCUT: {
    title: 'Rate Parity Undercut',
    description: "Today's OTA prices lower than the direct price.",
  },
  RATE_PARITY_LOWER: {
    title: 'Rate Parity Lower',
    description: 'Count of OTA prices lower than the direct price.',
  },
  REVENUE_AT_RISK: {
    title: 'Revenue at Risk',
    description: 'Estimated revenue loss due to OTAs undercutting your direct rates.',
  },
  PRICE_RECOMMENDATION: {
    title: 'Dynamic Price Recommendation',
    description: 'AI-powered rate recommendation based on competitor pricing, market occupancy, and local events.',
  },
  COMPETITOR_ANALYSIS: {
    title: 'Competitor Price Analysis',
    description: 'Compares your pricing against individual competitors and the market average.',
  },
  DEMAND_SCORE: {
    title: 'Demand Score Index',
    description: 'Indicates relative demand level based on market pricing signals and seasonality.',
  },
  AI_INSIGHT_GENERATION: {
    title: 'AI Insight Generation',
    description: 'AI-generated insights analyzing market conditions and providing actionable recommendations.',
  },
  OTA_COMPLIANCE_RATE: {
    title: 'OTA Compliance Rate',
    description: 'Percentage of time an OTA maintains rate parity with your direct rate.',
  },
  MARKET_SHARE: {
    title: 'Estimated Market Share',
    description: 'Estimated share of market based on review volume as a proxy for bookings.',
  },
};
